import * as React from "react";
import PropTypes from "prop-types";
import { Input as BaseInput } from "@mui/base/Input";
import { Box, styled } from "@mui/system";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import axios from "constants/axios";
import { isAxiosError } from "axios";

// ─── OTP Boxes Component (unchanged) ─────────────────────────────────────────
function OTP({ open, separator, length, value, onChange }) {
  const inputRefs = React.useRef(new Array(length).fill(null));

  const focusInput = (targetIndex) => {
    inputRefs.current[targetIndex].focus();
  };
  React.useEffect(() => {
    if (open) {
      inputRefs.current[0].focus();
    }
  }, [open]);

  const selectInput = (targetIndex) => {
    inputRefs.current[targetIndex].select();
  };

  const handleKeyDown = (event, currentIndex) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange((prevOtp) => {
          return (
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1)
          );
        });
        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        onChange((prevOtp) => {
          return (
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1)
          );
        });
        break;
      default:
        break;
    }
  };

  const handleChange = (event, currentIndex) => {
    const currentValue = event.target.value.replace(/\D/g, ""); // ← strip non-digits

    if (!currentValue) return; // ← ignore if nothing left after stripping

    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1;
      } else {
        break;
      }
    }

    onChange((prev) => {
      const otpArray = prev.split("");
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });

    if (currentValue !== "" && currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    }
  };

  const handleClick = (event, currentIndex) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event, currentIndex) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");
      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? " ";
        otpArray[i] = lastValue;
      }
      onChange(otpArray.join(""));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <BaseInput
            slots={{ input: InputElement }}
            aria-label={`Digit ${index + 1} of OTP`}
            slotProps={{
              input: {
                ref: (ele) => {
                  inputRefs.current[index] = ele;
                },
                onKeyDown: (event) => handleKeyDown(event, index),
                onChange: (event) => handleChange(event, index),
                onClick: (event) => handleClick(event, index),
                onPaste: (event) => handlePaste(event, index),
                value: value[index] ?? "",
                inputMode: "numeric", // ← shows numeric keyboard on mobile
                pattern: "[0-9]*",
              },
            }}
          />
          {index === length - 1 ? null : separator}
        </React.Fragment>
      ))}
    </Box>
  );
}

OTP.propTypes = {
  length: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  separator: PropTypes.node,
  value: PropTypes.string.isRequired,
};

// ─── OTPInput Dialog Component ────────────────────────────────────────────────
export default function OTPInput({ open, phone, onSuccess, handleClose }) {
  const [otp, setOtp] = React.useState("");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);

  // Reset OTP when dialog opens
  React.useEffect(() => {
    if (open) setOtp("");
  }, [open]);

  const handleVerify = async () => {
    if (otp.length !== 4) {
      NotificationManager.error("Please enter the complete 4-digit OTP");
      return;
    }

    setIsVerifying(true);
    try {
      const { data } = await axios.post("/tempUser/verifyOtp", { phone, otp });
      if (data?.error) {
        throw new Error(data?.title);
      }
      NotificationManager.success(data?.title ?? "OTP verified!");
      onSuccess(data?.token);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : isAxiosError(err)
            ? err?.response?.data?.message
            : "Invalid OTP, please try again";
      console.error(msg);
      NotificationManager.error(msg);
      setOtp("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      const { data } = await axios.get(`/tempUser/resendOtp?phone=${phone}`);
      if (data?.error) {
        throw new Error(data?.title);
      }
      NotificationManager.success(
        data?.title ?? "OTP resent to your WhatsApp!",
      );
      setOtp("");
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : isAxiosError(err)
            ? err?.response?.data?.message
            : "Failed to send otp";
      console.error(msg);
      NotificationManager.error(msg);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={isVerifying}
    >
      {/* Title — matches UserInfoDialog style */}
      <DialogTitle>
        <h2 style={{ margin: 0, textAlign: "center", fontSize: "1.5rem" }}>
          Verify Your Number
        </h2>
        <p
          style={{
            margin: "8px 0 0",
            textAlign: "center",
            fontSize: "0.875rem",
            color: "#666",
          }}
        >
          Enter the 4-digit OTP sent to <strong>+91 {phone}</strong> on WhatsApp
        </p>
      </DialogTitle>

      <DialogContent>
        {/* OTP Boxes */}
        <Box mt={2} mb={3}>
          <OTP
            open={open}
            separator={<span>-</span>}
            value={otp}
            onChange={setOtp}
            length={4}
          />
        </Box>

        {/* Verify Button */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleVerify}
          disabled={isVerifying || otp.length !== 4}
          style={{ marginBottom: "12px", padding: "12px" }}
        >
          {isVerifying ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Verify OTP"
          )}
        </Button>

        {/* Resend + Cancel */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            color="secondary"
            onClick={handleClose}
            disabled={isVerifying}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleResend}
            disabled={isResending || isVerifying}
            size="small"
          >
            {isResending ? "Resending..." : "Resend OTP"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

OTPInput.propTypes = {
  open: PropTypes.bool.isRequired,
  phone: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
};

// ─── Styles (unchanged) ───────────────────────────────────────────────────────
const blue = {
  100: "#DAECFF",
  200: "#80BFFF",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  700: "#0059B2",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const InputElement = styled("input")(
  ({ theme }) => `
  width: 40px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 0;
  border-radius: 8px;
  text-align: center;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0 2px 4px ${
    theme.palette.mode === "dark" ? "rgba(0,0,0, 0.5)" : "rgba(0,0,0, 0.05)"
  };

  &:hover { border-color: ${blue[400]}; }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${
      theme.palette.mode === "dark" ? blue[600] : blue[200]
    };
  }

  &:focus-visible { outline: 0; }
`,
);
